import { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Check } from 'lucide-react';
import { WEEKLY_BLOCKED, WEEKLY_BLOCK_NOTE } from './types';
import type { BonusFrequency, LeagueBonus } from './types';

export interface BonusFormValues {
  bonus_name: string;
  description: string;
  amount: number;
  frequency: BonusFrequency;
}

/**
 * Add Custom / Edit modal. Custom-bonus shape is always trigger_type='manual'
 * and icon='sparkles' — preset-derived bonuses get added via PresetPickerDialog.
 */
export function BonusFormDialog({
  open,
  onOpenChange,
  initial,
  onSave,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initial: LeagueBonus | null;
  onSave: (values: BonusFormValues) => void;
}) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [amount, setAmount] = useState('');
  // SAB-632 STEP 0: 'per_week' (weekly) is blocked until SAB-648; default to per_season.
  const [frequency, setFrequency] = useState<BonusFrequency>('per_season');

  useEffect(() => {
    if (open) {
      setName(initial?.bonus_name ?? '');
      setDescription(initial?.description ?? '');
      setAmount(initial ? String(initial.amount) : '');
      const initFreq = initial?.frequency ?? 'per_season';
      setFrequency(WEEKLY_BLOCKED && initFreq === 'per_week' ? 'per_season' : initFreq);
    }
  }, [open, initial]);

  const valid = name.trim().length > 0 && parseFloat(amount) > 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {initial ? `Edit: ${initial.bonus_name}` : 'Add Custom Bonus'}
          </DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-4">
          <FieldRow label="Bonus name">
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Most Penalties"
            />
          </FieldRow>

          <FieldRow
            label="Description"
            sub="Shown to managers in activity feed and standings"
          >
            <Textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Awarded when..."
              rows={3}
            />
          </FieldRow>

          <div className="grid grid-cols-2 gap-3">
            <FieldRow label="Amount ($)">
              <Input
                type="number"
                min={1}
                step={1}
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
              />
            </FieldRow>
            <FieldRow label="Frequency">
              <Select
                value={frequency}
                onValueChange={(v) => setFrequency(v as BonusFrequency)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {/* SAB-648 2026-08-22: weekly is UNBLOCKED. The gate stays so it can be re-closed for
                      unlocked leagues if needed — it cannot be re-closed for one already locked. */}
                  {!WEEKLY_BLOCKED && <SelectItem value="per_week">Per week</SelectItem>}
                  <SelectItem value="per_season">Per season</SelectItem>
                  <SelectItem value="one_time">One-time</SelectItem>
                </SelectContent>
              </Select>
              {WEEKLY_BLOCKED && (
                <p className="text-[11px] text-muted-foreground mt-1">Weekly: {WEEKLY_BLOCK_NOTE}</p>
              )}
            </FieldRow>
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            disabled={!valid}
            onClick={() => {
              onSave({
                bonus_name: name.trim(),
                description: description.trim(),
                amount: Math.max(0, parseFloat(amount) || 0),
                frequency,
              });
            }}
            className="gap-1"
          >
            <Check className="w-3.5 h-3.5" /> Save bonus
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function FieldRow({
  label,
  sub,
  children,
}: {
  label: string;
  sub?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="flex flex-col gap-1.5">
      <div>
        <Label className="text-[11px] font-bold uppercase tracking-[0.06em] text-muted-foreground">
          {label}
        </Label>
        {sub && (
          <div className="text-[11px] text-muted-foreground/70 mt-0.5">
            {sub}
          </div>
        )}
      </div>
      {children}
    </label>
  );
}
